import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cliente, User } from '../../types';
import { clientsAPI, usersAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import toast from 'react-hot-toast';
import {
  Globe,
  UserPlus,
  UserX,
  Eye,
  EyeOff,
  Loader2,
  Shield,
  UserCog,
  KeyRound,
  Power,
  PowerOff,
  LogIn,
  Lock,
} from 'lucide-react';

interface ClientPortalTabProps {
  client: Cliente;
  onUpdate: () => void;
}

export default function ClientPortalTab({ client, onUpdate }: ClientPortalTabProps) {
  const queryClient = useQueryClient();
  const { tenant, user: currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const [showInviteConfirm, setShowInviteConfirm] = useState(false);
  const [showActivateForm, setShowActivateForm] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [showResetPasswordForm, setShowResetPasswordForm] = useState(false);
  const [activatePassword, setActivatePassword] = useState('');
  const [resetPassword, setResetPassword] = useState('');

  const hasAccess = !!client.has_portal_access;
  const isPortalActive = client.portal_user_active === true;
  const isPortalDeactivated = hasAccess && client.portal_user_active === false;

  // Fetch tenant users for agent dropdown (commerciale/manager roles)
  const { data: usersData } = useQuery({
    queryKey: ['users', 'agent-select', tenant?.id, currentUser?.id, client.tenant_id],
    enabled: !!tenant?.id && tenant.id === client.tenant_id,
    queryFn: async () => {
      const res = await usersAPI.getUsers({ limit: 100, is_active: true, tenant_id: client.tenant_id });
      return res.data.data.filter(
        (u: User) => u.tenant_id === client.tenant_id && (u.role === 'commerciale' || u.role === 'manager' || u.role === 'admin')
      );
    },
  });

  const invalidateAndUpdate = () => {
    queryClient.invalidateQueries({ queryKey: ['client', client.id] });
    queryClient.invalidateQueries({ queryKey: ['clients'] });
    queryClient.invalidateQueries({ queryKey: ['clienti', 'select'] });
    queryClient.invalidateQueries({ queryKey: ['clients-select'] });
    onUpdate();
  };

  const inviteMutation = useMutation({
    mutationFn: (data?: { email_override?: string; agente_id?: string }) =>
      clientsAPI.inviteToPortal(client.id, data),
    onSuccess: () => {
      toast.success('Cliente invitato al portale con successo');
      setShowInviteConfirm(false);
      invalidateAndUpdate();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'invito");
    },
  });

  const activateMutation = useMutation({
    mutationFn: (data: { password: string; email_override?: string; agente_id?: string }) =>
      clientsAPI.activatePortal(client.id, data),
    onSuccess: () => {
      toast.success('Portale attivato con successo');
      setShowActivateForm(false);
      setActivatePassword('');
      invalidateAndUpdate();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'attivazione");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: () => clientsAPI.revokeAccess(client.id),
    onSuccess: () => {
      toast.success('Accesso al portale revocato');
      setShowRevokeConfirm(false);
      invalidateAndUpdate();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella revoca');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () => clientsAPI.deactivatePortal(client.id),
    onSuccess: () => {
      toast.success('Portale disattivato');
      setShowDeactivateConfirm(false);
      invalidateAndUpdate();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella disattivazione');
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () => clientsAPI.reactivatePortal(client.id),
    onSuccess: () => {
      toast.success('Portale riattivato');
      setShowReactivateConfirm(false);
      invalidateAndUpdate();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella riattivazione');
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (data: { new_password: string }) =>
      clientsAPI.resetPortalPassword(client.id, data),
    onSuccess: () => {
      toast.success('Password reimpostata con successo');
      setShowResetPasswordForm(false);
      setResetPassword('');
      invalidateAndUpdate();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel reset password');
    },
  });

  const impersonateMutation = useMutation({
    mutationFn: () => clientsAPI.impersonateClient(client.id),
    onSuccess: (response) => {
      const data = response.data;
      // Store new tokens and redirect to portal
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      // Reload to apply new auth context
      window.location.href = '/catalog';
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'impersonazione");
    },
  });

  const updateAgentMutation = useMutation({
    mutationFn: (agente_id: string | null) =>
      clientsAPI.updateClient(client.id, { agente_id }),
    onSuccess: () => {
      toast.success('Agente assegnato');
      invalidateAndUpdate();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'assegnazione agente");
    },
  });

  const togglePrezziMutation = useMutation({
    mutationFn: (mostra_prezzi: boolean) =>
      clientsAPI.updateClient(client.id, { mostra_prezzi }),
    onSuccess: () => {
      toast.success('Impostazione prezzi aggiornata');
      invalidateAndUpdate();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    },
  });

  // Status badge
  const getStatusBadge = () => {
    if (!hasAccess) {
      return { label: 'Non attivo', className: 'bg-gray-100 text-gray-600' };
    }
    if (isPortalDeactivated) {
      return { label: 'Sospeso', className: 'bg-amber-100 text-amber-800' };
    }
    return { label: 'Attivo', className: 'bg-green-100 text-green-800' };
  };

  const statusBadge = getStatusBadge();

  return (
    <div className="space-y-6">
      {/* Portal Access Status */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Globe className="h-5 w-5 text-gray-500" />
            Accesso Portale
          </h3>
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${statusBadge.className}`}
          >
            <Shield className="h-4 w-4" />
            {statusBadge.label}
          </span>
        </div>

        {!hasAccess && (
          <p className="text-sm text-gray-500 mb-4">
            Il cliente non ha accesso al portale. Invitalo o attivalo impostando una password.
          </p>
        )}

        {hasAccess && isPortalActive && (
          <p className="text-sm text-gray-500 mb-4">
            Il cliente ha un account portale collegato e attivo.
          </p>
        )}

        {isPortalDeactivated && (
          <p className="text-sm text-amber-600 mb-4">
            L'accesso al portale del cliente è sospeso. Riattivalo per consentire l'accesso.
          </p>
        )}

        {/* Action Buttons - No Access */}
        {!hasAccess && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              {/* Invite button (sends email) */}
              {!showInviteConfirm && !showActivateForm && (
                <>
                  <button
                    onClick={() => setShowInviteConfirm(true)}
                    disabled={!client.email}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    <UserPlus className="h-4 w-4" />
                    Invita al Portale
                  </button>
                  <button
                    onClick={() => setShowActivateForm(true)}
                    disabled={!client.email}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    <KeyRound className="h-4 w-4" />
                    Attiva con Password
                  </button>
                </>
              )}
              {!client.email && !showInviteConfirm && !showActivateForm && (
                <p className="text-xs text-amber-600 self-center">
                  Il cliente deve avere un indirizzo email.
                </p>
              )}
            </div>

            {/* Invite confirmation */}
            {showInviteConfirm && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800 flex-1">
                  Verrà creato un account per <strong>{client.email}</strong>. Confermi?
                </p>
                <button
                  onClick={() => inviteMutation.mutate(undefined)}
                  disabled={inviteMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                  {inviteMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Conferma'
                  )}
                </button>
                <button
                  onClick={() => setShowInviteConfirm(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                >
                  Annulla
                </button>
              </div>
            )}

            {/* Activate with password form */}
            {showActivateForm && (
              <div className="p-4 bg-green-50 rounded-lg space-y-3">
                <p className="text-sm text-green-800">
                  Imposta una password per l'account <strong>{client.email}</strong>.
                </p>
                <div className="flex items-end gap-3">
                  <div className="flex-1 max-w-xs">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      value={activatePassword}
                      onChange={(e) => setActivatePassword(e.target.value)}
                      placeholder="Minimo 8 caratteri"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                    />
                  </div>
                  <button
                    onClick={() => activateMutation.mutate({ password: activatePassword })}
                    disabled={activateMutation.isPending || activatePassword.length < 8}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                  >
                    {activateMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Attiva'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowActivateForm(false);
                      setActivatePassword('');
                    }}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Annulla
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons - Has Access */}
        {hasAccess && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              {/* Deactivate / Reactivate */}
              {isPortalActive && !showDeactivateConfirm && !showRevokeConfirm && !showResetPasswordForm && (
                <button
                  onClick={() => setShowDeactivateConfirm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 text-sm font-medium"
                >
                  <PowerOff className="h-4 w-4" />
                  Disattiva
                </button>
              )}

              {isPortalDeactivated && !showReactivateConfirm && !showRevokeConfirm && !showResetPasswordForm && (
                <button
                  onClick={() => setShowReactivateConfirm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 text-sm font-medium"
                >
                  <Power className="h-4 w-4" />
                  Riattiva
                </button>
              )}

              {/* Reset Password */}
              {!showResetPasswordForm && !showDeactivateConfirm && !showReactivateConfirm && !showRevokeConfirm && (
                <button
                  onClick={() => setShowResetPasswordForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 text-sm font-medium"
                >
                  <Lock className="h-4 w-4" />
                  Reset Password
                </button>
              )}

              {/* Impersonate */}
              {isPortalActive && !showDeactivateConfirm && !showRevokeConfirm && !showResetPasswordForm && hasPermission('clienti:impersonate') && (
                <button
                  onClick={() => impersonateMutation.mutate()}
                  disabled={impersonateMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 text-sm font-medium disabled:opacity-50"
                >
                  {impersonateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LogIn className="h-4 w-4" />
                  )}
                  Impersona Cliente
                </button>
              )}

              {/* Revoke (permanent) */}
              {!showRevokeConfirm && !showDeactivateConfirm && !showReactivateConfirm && !showResetPasswordForm && (
                <button
                  onClick={() => setShowRevokeConfirm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 text-sm font-medium"
                >
                  <UserX className="h-4 w-4" />
                  Revoca Accesso
                </button>
              )}
            </div>

            {/* Deactivate confirmation */}
            {showDeactivateConfirm && (
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                <p className="text-sm text-amber-800 flex-1">
                  L'accesso al portale verrà sospeso. Il cliente non potrà accedere fino alla riattivazione.
                </p>
                <button
                  onClick={() => deactivateMutation.mutate()}
                  disabled={deactivateMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50"
                >
                  {deactivateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Conferma'
                  )}
                </button>
                <button
                  onClick={() => setShowDeactivateConfirm(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                >
                  Annulla
                </button>
              </div>
            )}

            {/* Reactivate confirmation */}
            {showReactivateConfirm && (
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800 flex-1">
                  L'accesso al portale verrà riattivato. Il cliente potrà accedere nuovamente.
                </p>
                <button
                  onClick={() => reactivateMutation.mutate()}
                  disabled={reactivateMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                >
                  {reactivateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Conferma'
                  )}
                </button>
                <button
                  onClick={() => setShowReactivateConfirm(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                >
                  Annulla
                </button>
              </div>
            )}

            {/* Reset password form */}
            {showResetPasswordForm && (
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <p className="text-sm text-gray-700">
                  Imposta una nuova password per l'account portale del cliente.
                </p>
                <div className="flex items-end gap-3">
                  <div className="flex-1 max-w-xs">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Nuova Password
                    </label>
                    <input
                      type="password"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      placeholder="Minimo 8 caratteri"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <button
                    onClick={() => resetPasswordMutation.mutate({ new_password: resetPassword })}
                    disabled={resetPasswordMutation.isPending || resetPassword.length < 8}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                  >
                    {resetPasswordMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Salva'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowResetPasswordForm(false);
                      setResetPassword('');
                    }}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Annulla
                  </button>
                </div>
              </div>
            )}

            {/* Revoke confirmation */}
            {showRevokeConfirm && (
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                <p className="text-sm text-red-800 flex-1">
                  L'account portale del cliente verrà eliminato definitivamente. Confermi?
                </p>
                <button
                  onClick={() => revokeMutation.mutate()}
                  disabled={revokeMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                >
                  {revokeMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Conferma'
                  )}
                </button>
                <button
                  onClick={() => setShowRevokeConfirm(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                >
                  Annulla
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Agent Assignment */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <UserCog className="h-5 w-5 text-gray-500" />
          Agente Assegnato
        </h3>

        <div className="flex items-center gap-4">
          <select
            value={client.agente_id || ''}
            onChange={(e) => updateAgentMutation.mutate(e.target.value || null)}
            disabled={updateAgentMutation.isPending}
            className="flex-1 max-w-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:opacity-50"
          >
            <option value="">Nessun agente</option>
            {usersData?.map((u: User) => (
              <option key={u.id} value={u.id}>
                {u.name || `${u.nome} ${u.cognome || ''}`} ({u.role})
              </option>
            ))}
          </select>
          {updateAgentMutation.isPending && (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          )}
        </div>
        {client.agente_nome && (
          <p className="text-sm text-gray-500 mt-2">
            Agente attuale: <span className="font-medium text-gray-700">{client.agente_nome}</span>
          </p>
        )}
      </div>

      {/* Price Visibility Toggle */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          {client.mostra_prezzi ? (
            <Eye className="h-5 w-5 text-gray-500" />
          ) : (
            <EyeOff className="h-5 w-5 text-gray-500" />
          )}
          Visibilità Prezzi
        </h3>

        <div className="flex items-center gap-4">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={client.mostra_prezzi || false}
              onChange={(e) => togglePrezziMutation.mutate(e.target.checked)}
              disabled={togglePrezziMutation.isPending}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            <span className="ml-3 text-sm text-gray-700">
              {client.mostra_prezzi ? 'Prezzi visibili nel catalogo' : 'Prezzi nascosti nel catalogo'}
            </span>
          </label>
          {togglePrezziMutation.isPending && (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Se abilitato, il cliente vedrà i prezzi dei prodotti nel catalogo del portale.
        </p>
      </div>
    </div>
  );
}
